import React, { useEffect, useState } from 'react';
import api from '../api';
import toast from 'react-hot-toast';
import {
    CreditCard, Check, Sparkles, RefreshCw,
    Zap, ShieldCheck, Settings, Plus, Trash2, ArrowLeft, X,
    ShoppingCart, Megaphone
} from 'lucide-react';
import { loadStripe } from '@stripe/stripe-js';
import { Elements } from '@stripe/react-stripe-js';
import StripePaymentForm from '../components/StripePaymentForm';
import StripeCardForm from '../components/StripeCardForm';

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLIC_KEY);

const BillingPage = () => {
    const [user, setUser] = useState(null);
    const [loadingUser, setLoadingUser] = useState(true);
    const [submittingPlan, setSubmittingPlan] = useState(null);
    const [clientSecret, setClientSecret] = useState(null);
    const [checkoutPlan, setCheckoutPlan] = useState(null);
    const [priceBasic, setPriceBasic] = useState(49.90);
    const [pricePremium, setPricePremium] = useState(99.90);
    const [priceDivulgadorBasic, setPriceDivulgadorBasic] = useState(39.90);
    const [priceDivulgadorPremium, setPriceDivulgadorPremium] = useState(79.90);
    const [planCategory, setPlanCategory] = useState('afiliados'); // 'afiliados' | 'divulgadores'

    // Estados para controle do Painel de Configuração de Cartões
    const [viewMode, setViewMode] = useState('plans'); // 'plans' ou 'config'
    const [cards, setCards] = useState([]);
    const [loadingCards, setLoadingCards] = useState(false);
    const [showAddCardForm, setShowAddCardForm] = useState(false);
    const [setupClientSecret, setSetupClientSecret] = useState(null);
    const [settingDefault, setSettingDefault] = useState(null);
    const [showCancelModal, setShowCancelModal] = useState(false);
    const [cancellingPlan, setCancellingPlan] = useState(false);

    // Novos estados para modal de alteração de plano e exclusão de cartões
    const [showConfirmModal, setShowConfirmModal] = useState(false);
    const [planToChange, setPlanToChange] = useState(null);
    const [showDeleteCardModal, setShowDeleteCardModal] = useState(false);
    const [cardToDelete, setCardToDelete] = useState(null);

    // Coupon and CPF states
    const [cpfInput, setCpfInput] = useState('');
    const [couponCodeInput, setCouponCodeInput] = useState('');
    const [appliedCoupon, setAppliedCoupon] = useState(null);
    const [isValidatingCoupon, setIsValidatingCoupon] = useState(false);

    // Trial and WhatsApp Verification States
    const [showTrialModal, setShowTrialModal] = useState(false);
    const [trialPlanToClaim, setTrialPlanToClaim] = useState(null);
    const [whatsappNumber, setWhatsappNumber] = useState('');
    const [whatsappCode, setWhatsappCode] = useState('');
    const [trialStep, setTrialStep] = useState(1);
    const [trialLoading, setTrialLoading] = useState(false);

    // Format CPF (000.000.000-00)
    const formatarCPF = (value) => {
        return value
            .replace(/\D/g, '')
            .replace(/(\d{3})(\d)/, '$1.$2')
            .replace(/(\d{3})(\d)/, '$1.$2')
            .replace(/(\d{3})(\d{1,2})$/, '$1-$2')
            .substring(0, 14);
    };

    const handleCpfChange = (e) => {
        setCpfInput(formatarCPF(e.target.value));
    };

    const handleApplyCoupon = async () => {
        if (!couponCodeInput.trim()) {
            toast.error("Por favor, insira o código do cupom.");
            return;
        }
        setIsValidatingCoupon(true);
        try {
            const res = await api.post('/payment/coupons/validate', {
                code: couponCodeInput,
                plan: planToChange
            });
            if (res.data && res.data.isValid) {
                setAppliedCoupon(res.data);
                toast.success(`Cupom ${res.data.code} aplicado! Desconto de ${res.data.discountPercentage}%.`);
            }
        } catch (err) {
            console.error(err);
            toast.error(err.response?.data?.error || "Cupom inválido ou expirado.");
            setAppliedCoupon(null);
        } finally {
            setIsValidatingCoupon(false);
        }
    };

    useEffect(() => {
        carregarPerfil();
        carregarPrecos();
    }, []);

    const carregarPrecos = async () => {
        try {
            const res = await api.get('/payment/prices');
            if (res.data && res.data.success) {
                setPriceBasic(res.data.priceBasic);
                setPricePremium(res.data.pricePremium);
                setPriceDivulgadorBasic(res.data.priceDivulgadorBasic || 39.90);
                setPriceDivulgadorPremium(res.data.priceDivulgadorPremium || 79.90);
            }
        } catch (err) {
            console.error("Erro ao carregar preços:", err);
        }
    };

    const carregarPerfil = async () => {
        setLoadingUser(true);
        try {
            const res = await api.get('/auth/me');
            setUser(res.data);
            if (res.data?.plan?.includes('divulgador')) {
                setPlanCategory('divulgadores');
            }
        } catch (err) {
            console.error(err);
            toast.error("Não foi possível carregar os dados do perfil.");
        } finally {
            setLoadingUser(false);
        }
    };

    // Busca os cartões reais cadastrados na conta do cliente via Stripe API
    const buscarCartoesStripe = async () => {
        setLoadingCards(true);
        try {
            const res = await api.get('/payment/cards');
            setCards(res.data.cards || []);
        } catch (err) {
            console.error(err);
            toast.error("Erro ao buscar seus cartões salvos.");
        } finally {
            setLoadingCards(false);
        }
    };

    const handleOpenConfig = () => {
        setViewMode('config');
        buscarCartoesStripe();
    };

    // Abre o modal de exclusão
    const handleRemoveCardClick = (paymentMethodId) => {
        setCardToDelete(paymentMethodId);
        setShowDeleteCardModal(true);
    };

    // Executa a exclusão de fato
    const handleRemoveCard = async () => {
        if (!cardToDelete) return;
        const paymentMethodId = cardToDelete;
        setShowDeleteCardModal(false);
        setCardToDelete(null);

        const loadToast = toast.loading("Removendo cartão...");
        try {
            await api.delete(`/payment/cards/${paymentMethodId}`);
            toast.success("Cartão removido com sucesso!", { id: loadToast });
            buscarCartoesStripe();
        } catch (err) {
            console.error(err);
            toast.error("Erro ao remover cartão.", { id: loadToast });
        }
    };

    const handleInitAddCard = async () => {
        const loadToast = toast.loading("Iniciando ambiente de cadastro seguro...");
        try {
            const res = await api.post('/payment/setup-intent');
            setSetupClientSecret(res.data.clientSecret);
            setShowAddCardForm(true);
            toast.dismiss(loadToast);
        } catch (err) {
            console.error(err);
            toast.error("Erro ao abrir formulário de adição de cartão.", { id: loadToast });
        }
    };

    // Abre o modal de alteração de plano se o plano for diferente
    const handlePlanClick = (plan) => {
        if (user?.plan === plan) {
            toast.error("Você já está neste plano.");
            return;
        }
        setPlanToChange(plan);
        setShowConfirmModal(true);
    };

    const handleSubscribe = async (plan) => {
        // Garantir proteção lógica dupla no frontend
        if (user?.plan === plan) {
            toast.error("Você já está neste plano.");
            return;
        }

        // Validate CPF if user does not have it saved
        let cleanCpf = null;
        if (plan !== 'free' && plan !== 'free_divulgador' && !user?.cpf) {
            const clean = cpfInput.replace(/\D/g, '');
            if (clean.length !== 11) {
                toast.error("Por favor, preencha um CPF válido.");
                return;
            }
            cleanCpf = clean;
        }

        setSubmittingPlan(plan);
        const loadToast = toast.loading(`Processando sua solicitação...`);

        try {
            const payload = { plan };
            if (cleanCpf) payload.cpf = cleanCpf;
            if (appliedCoupon) payload.couponCode = appliedCoupon.code;

            const res = await api.post('/payment/checkout', payload);

            // Verifica se o backend indicou sucesso (abrange nova assinatura ou troca de plano)
            if (res.data && res.data.success) {
                toast.success(res.data.message || "Plano atualizado com sucesso!", { id: loadToast });
                // Reset fields
                setAppliedCoupon(null);
                setCouponCodeInput('');
                setCpfInput('');
                carregarPerfil();
            }
            // Lógica de 3D Secure (Autenticação extra)
            else if (res.data && res.data.clientSecret) {
                toast.dismiss(loadToast);
                setClientSecret(res.data.clientSecret);
                setCheckoutPlan(plan);
            }
            else if (res.data && res.data.invoiceUrl) {
                window.location.href = res.data.invoiceUrl;
            }
            else {
                throw new Error("Resposta do servidor inválida.");
            }
        } catch (err) {
            toast.error(err.response?.data?.error || "Erro ao conectar com Stripe.", { id: loadToast });
        } finally {
            setSubmittingPlan(null);
        }
    };

    const handleSetDefaultCard = async (paymentMethodId) => {
        setSettingDefault(paymentMethodId);
        const loadToast = toast.loading("Definindo cartão como principal...");
        try {
            await api.post('/payment/default-card', { paymentMethodId });
            toast.success("Cartão principal atualizado!", { id: loadToast });
            buscarCartoesStripe(); // Recarrega a lista para atualizar os status visuais
        } catch (err) {
            console.error(err);
            toast.error("Erro ao definir cartão principal.", { id: loadToast });
        } finally {
            setSettingDefault(null);
        }
    };

    const handleToggleAutoRenewal = async () => {
        const novoStatus = user?.cancel_at_period_end ? true : false;
        const loadToast = toast.loading(novoStatus ? "Ativando renovação..." : "Desativando renovação...");
        try {
            const res = await api.post('/payment/toggle-auto-renewal', { autoRenew: novoStatus });
            toast.success(res.data.message, { id: loadToast });
            carregarPerfil();
        } catch (err) {
            console.error(err);
            toast.error("Erro ao alterar renovação.", { id: loadToast });
        }
    };

    const handleCancelPlan = async () => {
        setCancellingPlan(true);
        const loadToast = toast.loading("Cancelando plano...");
        try {
            const res = await api.post('/payment/cancel');
            toast.success(res.data.message, { id: loadToast });
            setShowCancelModal(false);
            carregarPerfil();
        } catch (err) {
            console.error(err);
            toast.error(err.response?.data?.error || "Erro ao cancelar plano.", { id: loadToast });
        } finally {
            setCancellingPlan(false);
        }
    };

    const handleClaimTrialClick = (plan) => {
        if (user?.plan !== 'free') {
            return toast.error("Você já possui um plano ativo.");
        }
        if (user?.trialUsed) {
            return toast.error("Você já utilizou sua amostra grátis.");
        }
        setTrialPlanToClaim(plan);
        if (user?.userWhatsappVerified) {
            setTrialStep(3); // Ready to claim
        } else {
            setTrialStep(1); // Ask for whatsapp
        }
        setShowTrialModal(true);
    };

    const handleSendWhatsappCode = async (e) => {
        e.preventDefault();
        if (!whatsappNumber) return toast.error("Digite seu número de WhatsApp.");
        setTrialLoading(true);
        try {
            await api.post('/auth/whatsapp/send-code', { whatsappNumber });
            toast.success("Código enviado para o seu WhatsApp!");
            setTrialStep(2);
        } catch (err) {
            toast.error(err.response?.data?.error || "Erro ao enviar código.");
        } finally {
            setTrialLoading(false);
        }
    };

    const handleVerifyWhatsappCode = async (e) => {
        e.preventDefault();
        if (!whatsappCode) return toast.error("Digite o código recebido.");
        setTrialLoading(true);
        try {
            await api.post('/auth/whatsapp/verify', { code: whatsappCode });
            toast.success("WhatsApp verificado com sucesso!");
            carregarPerfil(); // Atualiza o state do user
            setTrialStep(3);
        } catch (err) {
            toast.error(err.response?.data?.error || "Código inválido.");
        } finally {
            setTrialLoading(false);
        }
    };

    const handleConfirmTrialClaim = async () => {
        setTrialLoading(true);
        try {
            const res = await api.post('/auth/trial/claim', { planId: trialPlanToClaim });
            toast.success(res.data.message);
            setShowTrialModal(false);
            carregarPerfil();
        } catch (err) {
            toast.error(err.response?.data?.error || "Erro ao ativar amostra grátis.");
        } finally {
            setTrialLoading(false);
        }
    };


    const getPlanPrice = (p) => {
        switch (p) {
            case 'basic': return priceBasic;
            case 'premium': return pricePremium;
            case 'basic_divulgador': return priceDivulgadorBasic;
            case 'premium_divulgador': return priceDivulgadorPremium;
            default: return 0;
        }
    };

    const formatarPlano = (p) => {
        switch (p) {
            case 'free': return 'Plano Free';
            case 'basic': return 'Plano Básico';
            case 'premium': return 'Plano Profissional';
            case 'free_divulgador': return 'Plano Free Divulgador';
            case 'basic_divulgador': return 'Plano Básico Divulgador';
            case 'premium_divulgador': return 'Plano Profissional Divulgador';
            default: return p;
        }
    };

    const formatarPreposicaoPlano = (p) => {
        switch (p) {
            case 'free': return 'Plano Free';
            case 'basic': return 'Plano Básico';
            case 'premium': return 'Premium';
            case 'free_divulgador': return 'Plano Free Divulgador';
            case 'basic_divulgador': return 'Plano Básico Divulgador';
            case 'premium_divulgador': return 'Plano Profissional Divulgador';
            default: return p;
        }
    };

    if (loadingUser) {
        return (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}>
                <style>{`
                    @keyframes spin { to { transform: rotate(360deg); } }
                    .custom-spin { animation: spin 1s linear infinite; }
                `}</style>
                <RefreshCw size={40} className="custom-spin" style={{ color: '#ee4d2d' }} />
            </div>
        );
    }

    return (
        <div style={{ maxWidth: '1140px', margin: '0 auto', fontFamily: 'Outfit, sans-serif', padding: '20px' }}>
            <style>{`
                @keyframes fadeIn { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
                @keyframes fadeInModal { from { opacity: 0; transform: scale(0.95); } to { opacity: 1; transform: scale(1); } }
                @keyframes spin { to { transform: rotate(360deg); } }
                .custom-spin { animation: spin 1s linear infinite; }
            `}</style>

            {/* Modal de Confirmação de Cancelamento */}
            {showCancelModal && (
                <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999, padding: '20px' }}>
                    <div style={{ backgroundColor: 'white', borderRadius: '20px', padding: '32px', maxWidth: '440px', width: '100%', boxShadow: '0 25px 50px -12px rgba(0,0,0,0.25)', animation: 'fadeInModal 0.2s ease-out' }}>
                        <div style={{ width: '52px', height: '52px', backgroundColor: '#fef2f2', borderRadius: '14px', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '20px' }}>
                            <X size={26} style={{ color: '#ef4444' }} />
                        </div>
                        <h2 style={{ margin: '0 0 10px 0', fontSize: '20px', fontWeight: '800', color: '#0f172a' }}>Cancelar Assinatura?</h2>
                        <p style={{ margin: '0 0 8px 0', fontSize: '14px', color: '#475569', lineHeight: '1.6' }}>
                            Você continuará tendo acesso a todos os recursos do seu plano <strong>{formatarPlano(user?.plan)}</strong> até o final do ciclo já pago.
                        </p>
                        <p style={{ margin: '0 0 28px 0', fontSize: '13px', color: '#94a3b8' }}>
                            Após esse período, sua conta voltará automaticamente para o Plano Free e nenhuma nova cobrança será realizada.
                        </p>
                        <div style={{ display: 'flex', gap: '12px' }}>
                            <button
                                onClick={() => setShowCancelModal(false)}
                                disabled={cancellingPlan}
                                style={{ flex: 1, padding: '12px', borderRadius: '10px', border: '1px solid #e2e8f0', backgroundColor: 'white', color: '#475569', fontWeight: '700', fontSize: '14px', cursor: 'pointer' }}
                            >
                                Voltar
                            </button>
                            <button
                                onClick={handleCancelPlan}
                                disabled={cancellingPlan}
                                style={{ flex: 1, padding: '12px', borderRadius: '10px', border: 'none', backgroundColor: '#ef4444', color: 'white', fontWeight: '700', fontSize: '14px', cursor: cancellingPlan ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', opacity: cancellingPlan ? 0.7 : 1 }}
                            >
                                {cancellingPlan ? <RefreshCw size={14} className="custom-spin" /> : null}
                                {cancellingPlan ? 'Cancelando...' : 'Sim, cancelar'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Modal Amostra Grátis / Verificar WhatsApp */}
            {showTrialModal && (
                <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999, padding: '20px' }}>
                    <div style={{ backgroundColor: 'white', borderRadius: '20px', padding: '32px', maxWidth: '440px', width: '100%', boxShadow: '0 25px 50px -12px rgba(0,0,0,0.25)', animation: 'fadeInModal 0.2s ease-out' }}>
                        <div style={{ width: '52px', height: '52px', backgroundColor: '#f0fdf4', borderRadius: '14px', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '20px' }}>
                            <Sparkles size={26} style={{ color: '#22c55e' }} />
                        </div>
                        <h2 style={{ margin: '0 0 10px 0', fontSize: '20px', fontWeight: '800', color: '#0f172a' }}>Ativar Amostra Grátis</h2>
                        
                        {trialStep === 1 && (
                            <form onSubmit={handleSendWhatsappCode}>
                                <p style={{ margin: '0 0 20px 0', fontSize: '14px', color: '#475569', lineHeight: '1.6' }}>
                                    Para ativar sua amostra grátis do <strong>{formatarPlano(trialPlanToClaim)}</strong>, precisamos verificar seu número de WhatsApp.
                                </p>
                                <div style={{ marginBottom: '20px' }}>
                                    <label style={{ display: 'block', fontSize: '13px', fontWeight: '700', color: '#334155', marginBottom: '6px' }}>Seu WhatsApp (com DDD)</label>
                                    <input
                                        type="text"
                                        placeholder="Ex: 11999999999"
                                        value={whatsappNumber}
                                        onChange={(e) => setWhatsappNumber(e.target.value)}
                                        style={{ width: '100%', padding: '10px 14px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '14px', outline: 'none' }}
                                        disabled={trialLoading}
                                    />
                                </div>
                                <div style={{ display: 'flex', gap: '12px' }}>
                                    <button type="button" onClick={() => setShowTrialModal(false)} style={{ flex: 1, padding: '12px', borderRadius: '10px', border: '1px solid #e2e8f0', backgroundColor: 'white', color: '#475569', fontWeight: '700', cursor: 'pointer' }}>Cancelar</button>
                                    <button type="submit" disabled={trialLoading} style={{ flex: 1, padding: '12px', borderRadius: '10px', border: 'none', backgroundColor: '#22c55e', color: 'white', fontWeight: '700', cursor: 'pointer' }}>{trialLoading ? 'Enviando...' : 'Enviar Código'}</button>
                                </div>
                            </form>
                        )}

                        {trialStep === 2 && (
                            <form onSubmit={handleVerifyWhatsappCode}>
                                <p style={{ margin: '0 0 20px 0', fontSize: '14px', color: '#475569', lineHeight: '1.6' }}>
                                    Enviamos um código de 6 dígitos para o WhatsApp <strong>{whatsappNumber}</strong>.
                                </p>
                                <div style={{ marginBottom: '20px' }}>
                                    <label style={{ display: 'block', fontSize: '13px', fontWeight: '700', color: '#334155', marginBottom: '6px' }}>Código Recebido</label>
                                    <input
                                        type="text"
                                        placeholder="000000"
                                        value={whatsappCode}
                                        onChange={(e) => setWhatsappCode(e.target.value)}
                                        style={{ width: '100%', padding: '10px 14px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '14px', outline: 'none' }}
                                        disabled={trialLoading}
                                        maxLength="6"
                                    />
                                </div>
                                <div style={{ display: 'flex', gap: '12px' }}>
                                    <button type="button" onClick={() => setTrialStep(1)} style={{ flex: 1, padding: '12px', borderRadius: '10px', border: '1px solid #e2e8f0', backgroundColor: 'white', color: '#475569', fontWeight: '700', cursor: 'pointer' }}>Voltar</button>
                                    <button type="submit" disabled={trialLoading} style={{ flex: 1, padding: '12px', borderRadius: '10px', border: 'none', backgroundColor: '#22c55e', color: 'white', fontWeight: '700', cursor: 'pointer' }}>{trialLoading ? 'Verificando...' : 'Verificar'}</button>
                                </div>
                            </form>
                        )}

                        {trialStep === 3 && (
                            <div>
                                <p style={{ margin: '0 0 20px 0', fontSize: '14px', color: '#475569', lineHeight: '1.6' }}>
                                    Tudo certo! Seu WhatsApp foi verificado. Clique abaixo para ativar seus dias de amostra grátis no <strong>{formatarPlano(trialPlanToClaim)}</strong>.
                                </p>
                                <div style={{ display: 'flex', gap: '12px' }}>
                                    <button type="button" onClick={() => setShowTrialModal(false)} style={{ flex: 1, padding: '12px', borderRadius: '10px', border: '1px solid #e2e8f0', backgroundColor: 'white', color: '#475569', fontWeight: '700', cursor: 'pointer' }}>Cancelar</button>
                                    <button type="button" onClick={handleConfirmTrialClaim} disabled={trialLoading} style={{ flex: 1, padding: '12px', borderRadius: '10px', border: 'none', backgroundColor: '#ee4d2d', color: 'white', fontWeight: '700', cursor: 'pointer' }}>{trialLoading ? 'Ativando...' : 'Ativar Amostra Grátis'}</button>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* Modal de Confirmação de Mudança de Plano */}
            {showConfirmModal && (
                <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999, padding: '20px' }}>
                    <div style={{ backgroundColor: 'white', borderRadius: '20px', padding: '32px', maxWidth: '440px', width: '100%', boxShadow: '0 25px 50px -12px rgba(0,0,0,0.25)', animation: 'fadeInModal 0.2s ease-out' }}>
                        <div style={{ width: '52px', height: '52px', backgroundColor: '#fff5f1', borderRadius: '14px', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '20px' }}>
                            <RefreshCw size={26} style={{ color: '#ee4d2d' }} />
                        </div>
                        <h2 style={{ margin: '0 0 10px 0', fontSize: '20px', fontWeight: '800', color: '#0f172a' }}>Confirmar Assinatura</h2>
                        <p style={{ margin: '0 0 20px 0', fontSize: '14px', color: '#475569', lineHeight: '1.6' }}>
                            Você está assinando o <strong>{formatarPreposicaoPlano(planToChange)}</strong>.
                        </p>

                        {/* Campo de CPF se o usuário não possuir um salvo */}
                        {!user?.cpf && planToChange !== 'free' && planToChange !== 'free_divulgador' && (
                            <div style={{ marginBottom: '20px' }}>
                                <label style={{ display: 'block', fontSize: '13px', fontWeight: '700', color: '#334155', marginBottom: '6px' }}>CPF para Faturamento (Obrigatório)</label>
                                <input
                                    type="text"
                                    placeholder="000.000.000-00"
                                    value={cpfInput}
                                    onChange={handleCpfChange}
                                    style={{ width: '100%', padding: '10px 14px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '14px', outline: 'none' }}
                                />
                            </div>
                        )}

                        {/* Campo de Cupom de Desconto */}
                        {planToChange !== 'free' && planToChange !== 'free_divulgador' && (
                            <div style={{ marginBottom: '20px' }}>
                                <label style={{ display: 'block', fontSize: '13px', fontWeight: '700', color: '#334155', marginBottom: '6px' }}>Cupom de Desconto (Opcional)</label>
                                <div style={{ display: 'flex', gap: '8px' }}>
                                    <input
                                        type="text"
                                        placeholder="EX: PROMO10"
                                        value={couponCodeInput}
                                        onChange={(e) => setCouponCodeInput(e.target.value.toUpperCase())}
                                        disabled={!!appliedCoupon}
                                        style={{ flex: 1, padding: '10px 14px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '14px', outline: 'none', textTransform: 'uppercase' }}
                                    />
                                    {appliedCoupon ? (
                                        <button
                                            type="button"
                                            onClick={() => { setAppliedCoupon(null); setCouponCodeInput(''); }}
                                            style={{ padding: '10px 14px', backgroundColor: '#fee2e2', color: '#ef4444', border: 'none', borderRadius: '8px', fontSize: '13px', fontWeight: '700', cursor: 'pointer' }}
                                        >
                                            Remover
                                        </button>
                                    ) : (
                                        <button
                                            type="button"
                                            onClick={handleApplyCoupon}
                                            disabled={isValidatingCoupon}
                                            style={{ padding: '10px 14px', backgroundColor: '#ee4d2d', color: 'white', border: 'none', borderRadius: '8px', fontSize: '13px', fontWeight: '700', cursor: 'pointer', opacity: isValidatingCoupon ? 0.7 : 1 }}
                                        >
                                            {isValidatingCoupon ? '...' : 'Aplicar'}
                                        </button>
                                    )}
                                </div>

                                {appliedCoupon && (
                                    <div style={{ marginTop: '8px', display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', color: '#16a34a', fontWeight: '700' }}>
                                        <Check size={14} /> Cupom aplicado: {appliedCoupon.code} (-{appliedCoupon.discountPercentage}%)
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Resumo Financeiro */}
                        {planToChange !== 'free' && planToChange !== 'free_divulgador' && (
                            <div style={{ backgroundColor: '#f8fafc', padding: '16px', borderRadius: '12px', border: '1px solid #e2e8f0', marginBottom: '25px', fontSize: '13px' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px', color: '#475569' }}>
                                    <span>Preço mensal original:</span>
                                    <span>R$ {getPlanPrice(planToChange).toFixed(2).replace('.', ',')}</span>
                                </div>
                                {appliedCoupon && (
                                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px', color: '#16a34a', fontWeight: '700' }}>
                                        <span>Desconto (-{appliedCoupon.discountPercentage}%):</span>
                                        <span>-R$ {(getPlanPrice(planToChange) * appliedCoupon.discountPercentage / 100).toFixed(2).replace('.', ',')}</span>
                                    </div>
                                )}
                                <div style={{ height: '1px', backgroundColor: '#e2e8f0', margin: '8px 0' }} />
                                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '15px', fontWeight: '800', color: '#0f172a' }}>
                                    <span>Valor total:</span>
                                    <span>
                                        R$ {
                                            appliedCoupon
                                                ? (getPlanPrice(planToChange) * (1 - appliedCoupon.discountPercentage / 100)).toFixed(2).replace('.', ',')
                                                : getPlanPrice(planToChange).toFixed(2).replace('.', ',')
                                        }
                                        {appliedCoupon && appliedCoupon.duration === 'once' ? ' (1º mês)' : '/mês'}
                                    </span>
                                </div>
                            </div>
                        )}

                        <div style={{ display: 'flex', gap: '12px' }}>
                            <button
                                onClick={() => {
                                    setShowConfirmModal(false);
                                    setPlanToChange(null);
                                    setAppliedCoupon(null);
                                    setCouponCodeInput('');
                                    setCpfInput('');
                                }}
                                style={{ flex: 1, padding: '12px', borderRadius: '10px', border: '1px solid #e2e8f0', backgroundColor: 'white', color: '#475569', fontWeight: '700', fontSize: '14px', cursor: 'pointer' }}
                            >
                                Cancelar
                            </button>
                            <button
                                onClick={() => {
                                    setShowConfirmModal(false);
                                    if (planToChange) {
                                        handleSubscribe(planToChange);
                                    }
                                }}
                                disabled={planToChange !== 'free' && planToChange !== 'free_divulgador' && !user?.cpf && cpfInput.replace(/\D/g, '').length !== 11}
                                style={{
                                    flex: 1,
                                    padding: '12px',
                                    borderRadius: '10px',
                                    border: 'none',
                                    backgroundColor: (planToChange !== 'free' && planToChange !== 'free_divulgador' && !user?.cpf && cpfInput.replace(/\D/g, '').length !== 11) ? '#cbd5e1' : '#ee4d2d',
                                    color: 'white',
                                    fontWeight: '700',
                                    fontSize: '14px',
                                    cursor: (planToChange !== 'free' && planToChange !== 'free_divulgador' && !user?.cpf && cpfInput.replace(/\D/g, '').length !== 11) ? 'not-allowed' : 'pointer',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    gap: '8px'
                                }}
                            >
                                Confirmar Assinatura
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Modal de Confirmação de Exclusão de Cartão */}
            {showDeleteCardModal && (
                <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999, padding: '20px' }}>
                    <div style={{ backgroundColor: 'white', borderRadius: '20px', padding: '32px', maxWidth: '440px', width: '100%', boxShadow: '0 25px 50px -12px rgba(0,0,0,0.25)', animation: 'fadeInModal 0.2s ease-out' }}>
                        <div style={{ width: '52px', height: '52px', backgroundColor: '#fef2f2', borderRadius: '14px', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '20px' }}>
                            <Trash2 size={26} style={{ color: '#ef4444' }} />
                        </div>
                        <h2 style={{ margin: '0 0 10px 0', fontSize: '20px', fontWeight: '800', color: '#0f172a' }}>Remover Cartão</h2>
                        <p style={{ margin: '0 0 28px 0', fontSize: '14px', color: '#475569', lineHeight: '1.6' }}>
                            Você tem certeza que deseja remover este cartão de crédito da sua conta? Esta ação não pode ser desfeita.
                        </p>
                        <div style={{ display: 'flex', gap: '12px' }}>
                            <button
                                onClick={() => {
                                    setShowDeleteCardModal(false);
                                    setCardToDelete(null);
                                }}
                                style={{ flex: 1, padding: '12px', borderRadius: '10px', border: '1px solid #e2e8f0', backgroundColor: 'white', color: '#475569', fontWeight: '700', fontSize: '14px', cursor: 'pointer' }}
                            >
                                Cancelar
                            </button>
                            <button
                                onClick={handleRemoveCard}
                                style={{ flex: 1, padding: '12px', borderRadius: '10px', border: 'none', backgroundColor: '#ef4444', color: 'white', fontWeight: '700', fontSize: '14px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}
                            >
                                Confirmar
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Cabeçalho */}
            <div style={{ textAlign: 'center', marginBottom: '35px' }}>
                <h1 style={{ fontSize: '32px', fontWeight: '800', color: '#0f172a', margin: '0 0 10px 0', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px' }}>
                    <CreditCard size={32} style={{ color: '#ee4d2d' }} /> Planos e Cobrança
                </h1>
                <p style={{ color: '#64748b', fontSize: '16px', maxWidth: '600px', margin: '0 auto' }}>
                    Escolha o plano ideal para a sua estrutura ou configure as suas opções de faturamento atuais.
                </p>
            </div>

            {/* Menu de Controle Superior */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', backgroundColor: 'white', border: '1px solid #e2e8f0', padding: '16px 24px', borderRadius: '16px', marginBottom: '35px', boxShadow: '0 2px 4px rgba(0,0,0,0.02)', gap: '15px', flexWrap: 'wrap' }}>
                <div>
                    <span style={{ fontSize: '13px', color: '#64748b', fontWeight: '500' }}>Plano Atual da sua Conta: </span>
                    <span style={{ 
                        fontSize: '13px', 
                        fontWeight: '700', 
                        padding: '5px 14px', 
                        borderRadius: '12px',
                        color: user?.plan === 'basic' ? '#f97316' : (user?.plan === 'premium' || user?.plan === 'admin') ? '#a855f7' : '#64748b',
                        backgroundColor: user?.plan === 'basic' ? '#fff7ed' : (user?.plan === 'premium' || user?.plan === 'admin') ? '#faf5ff' : '#f1f5f9'
                    }}>
                        {formatarPlano(user?.plan || 'free')}
                        {user?.cancel_at_period_end && user?.plan_signed_at && (
                            ` (Expira em: ${(() => {
                                const d = new Date(user.plan_signed_at);
                                d.setDate(d.getDate() + 30);
                                return d.toLocaleDateString('pt-BR');
                            })()})`
                        )}
                    </span>
                </div>

                {viewMode === 'plans' ? (
                    <button
                        onClick={handleOpenConfig}
                        style={{
                            display: 'flex', alignItems: 'center', gap: '8px', backgroundColor: '#1e293b',
                            border: 'none', color: 'white', padding: '10px 18px', borderRadius: '10px',
                            fontSize: '13px', fontWeight: '700', cursor: 'pointer', transition: '0.2s'
                        }}
                    >
                        <Settings size={16} /> Configurar Pagamentos
                    </button>
                ) : (
                    <button
                        onClick={() => setViewMode('plans')}
                        style={{
                            display: 'flex', alignItems: 'center', gap: '8px', backgroundColor: 'white',
                            border: '1px solid #cbd5e1', color: '#475569', padding: '10px 18px', borderRadius: '10px',
                            fontSize: '13px', fontWeight: '700', cursor: 'pointer', transition: '0.2s'
                        }}
                    >
                        <ArrowLeft size={16} /> Voltar para os Planos
                    </button>
                )}
            </div>

            {/* ========================================================================= */}
            {/* PAINEL: CONFIGURAR PAGAMENTOS (CARTÕES REAIS DO STRIPE)                   */}
            {/* ========================================================================= */}
            {viewMode === 'config' && (
                <div style={{ maxWidth: '650px', margin: '0 auto', animation: 'fadeIn 0.3s ease-out' }}>
                    <div style={{ backgroundColor: 'white', border: '1px solid #e2e8f0', borderRadius: '20px', padding: '24px', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.02)' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', borderBottom: '1px solid #f1f5f9', paddingBottom: '15px', gap: '10px', flexWrap: 'wrap' }}>
                            <div>
                                <h2 style={{ fontSize: '18px', fontWeight: '800', color: '#0f172a', margin: 0 }}>Seus Cartões Salvos</h2>
                                <p style={{ margin: '3px 0 0 0', fontSize: '13px', color: '#64748b' }}>Métodos vinculados à sua assinatura Stripe.</p>
                            </div>
                            <button
                                onClick={handleInitAddCard}
                                style={{ display: 'flex', alignItems: 'center', gap: '6px', backgroundColor: '#ee4d2d', color: 'white', border: 'none', padding: '8px 14px', borderRadius: '8px', fontSize: '12px', fontWeight: '700', cursor: 'pointer' }}
                            >
                                <Plus size={14} /> Adicionar Cartão
                            </button>
                        </div>

                        {loadingCards ? (
                            <div style={{ display: 'flex', justifyContent: 'center', padding: '30px' }}>
                                <RefreshCw className="custom-spin" style={{ color: '#ee4d2d' }} />
                            </div>
                        ) : cards.length === 0 ? (
                            <div style={{ textAlign: 'center', padding: '40px 10px', color: '#64748b' }}>
                                <CreditCard size={40} style={{ color: '#cbd5e1', marginBottom: '12px' }} />
                                <p style={{ margin: 0, fontSize: '14px', fontWeight: '500' }}>Nenhum cartão de crédito cadastrado na sua conta.</p>
                            </div>
                        ) : (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                                {cards.map((card) => (
                                    <div key={card.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', border: card.isDefault ? '2px solid #ee4d2d' : '1px solid #e2e8f0', padding: '14px 18px', borderRadius: '12px', backgroundColor: '#f8fafc', position: 'relative' }}>

                                        {card.isDefault && (
                                            <span style={{ position: 'absolute', top: '-10px', left: '15px', backgroundColor: '#ee4d2d', color: 'white', fontSize: '10px', fontWeight: '800', padding: '2px 8px', borderRadius: '6px', textTransform: 'uppercase' }}>
                                                Principal
                                            </span>
                                        )}

                                        <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
                                            <div style={{ backgroundColor: 'white', padding: '6px 12px', borderRadius: '6px', border: '1px solid #e2e8f0', fontWeight: '800', fontSize: '11px', color: '#1e293b', textTransform: 'uppercase', letterSpacing: '0.03em' }}>
                                                {card.brand}
                                            </div>
                                            <div>
                                                <p style={{ margin: 0, fontSize: '14px', fontWeight: '700', color: '#0f172a' }}>
                                                    •••• •••• •••• {card.last4}
                                                </p>
                                                <p style={{ margin: '2px 0 0 0', fontSize: '12px', color: '#64748b' }}>
                                                    Validade: {card.exp_month < 10 ? `0${card.exp_month}` : card.exp_month}/{card.exp_year}
                                                </p>
                                            </div>
                                        </div>

                                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>

                                            {/* SE FOR O PRINCIPAL: Mostra apenas um texto ou tag estilizada */}
                                            {card.isDefault ? (
                                                <span style={{
                                                    backgroundColor: '#f0fdf4', // verde bem claro
                                                    color: '#16a34a', // texto verde
                                                    border: '1px solid #bbf7d0',
                                                    padding: '6px 12px',
                                                    borderRadius: '8px',
                                                    fontSize: '12px',
                                                    fontWeight: '600'
                                                }}>
                                                    Cartão Principal
                                                </span>
                                            ) : (
                                                /* SE NÃO FOR O PRINCIPAL: Mostra o botão para o usuário clicar e alterar */
                                                <button
                                                    onClick={() => handleSetDefaultCard(card.id)}
                                                    disabled={settingDefault !== null}
                                                    style={{
                                                        backgroundColor: 'white',
                                                        border: '1px solid #cbd5e1',
                                                        color: '#475569',
                                                        cursor: 'pointer',
                                                        padding: '6px 12px',
                                                        borderRadius: '8px',
                                                        fontSize: '12px',
                                                        fontWeight: '600',
                                                        transition: '0.2s'
                                                    }}
                                                >
                                                    {settingDefault === card.id ? 'Salvando...' : 'Usar como principal'}
                                                </button>
                                            )}

                                            {/* Seu botão de excluir original */}
                                            <button
                                                onClick={() => handleRemoveCardClick(card.id)}
                                                style={{ background: 'transparent', border: 'none', color: '#ef4444', cursor: 'pointer', padding: '6px', borderRadius: '6px' }}
                                            >
                                                <Trash2 size={16} />
                                            </button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                    {/* Gerenciar Assinatura */}
                    {user?.plan !== 'free' && user?.plan !== 'admin' && (
                        <div style={{ marginTop: '20px', backgroundColor: 'white', border: '1px solid #e2e8f0', borderRadius: '20px', padding: '24px', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.02)' }}>
                            <div style={{ marginBottom: '20px', borderBottom: '1px solid #f1f5f9', paddingBottom: '15px' }}>
                                <h2 style={{ fontSize: '18px', fontWeight: '800', color: '#0f172a', margin: 0 }}>Gerenciar Assinatura</h2>
                                <p style={{ margin: '3px 0 0 0', fontSize: '13px', color: '#64748b' }}>Configure sua renovação ou cancele seu plano atual.</p>
                            </div>

                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                                <div>
                                    <h3 style={{ margin: 0, fontSize: '15px', fontWeight: '700', color: '#1e293b' }}>Renovação Automática</h3>
                                    <p style={{ margin: '2px 0 0 0', fontSize: '13px', color: '#64748b' }}>
                                        {user?.cancel_at_period_end ? 'Sua assinatura será cancelada no fim do período.' : 'Sua assinatura será renovada automaticamente no próximo mês.'}
                                    </p>
                                </div>

                                {/* Interruptor padronizado igual ao primeiro */}
                                <button
                                    onClick={handleToggleAutoRenewal}
                                    style={{
                                        width: '60px',
                                        height: '30px',
                                        borderRadius: '15px',
                                        backgroundColor: !user?.cancel_at_period_end ? '#25d366' : '#cbd5e1',
                                        border: 'none',
                                        cursor: 'pointer',
                                        position: 'relative',
                                        transition: '0.3s'
                                    }}
                                >
                                    <div style={{
                                        width: '22px',
                                        height: '22px',
                                        backgroundColor: '#fff',
                                        borderRadius: '50%',
                                        position: 'absolute',
                                        top: '4px',
                                        left: !user?.cancel_at_period_end ? '34px' : '4px',
                                        transition: '0.3s',
                                        boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                                    }} />
                                </button>
                            </div>
                        </div>
                    )}

                    {/* Injeção do Formulário de Registro do Stripe Elements */}
                    {showAddCardForm && setupClientSecret && (
                        <div style={{ marginTop: '20px', backgroundColor: 'white', border: '1px solid #cbd5e1', borderRadius: '16px', padding: '24px', boxShadow: '0 10px 15px -3px rgba(0,0,0,0.05)', animation: 'fadeIn 0.3s ease-out' }}>
                            <h3 style={{ fontSize: '16px', fontWeight: '700', margin: '0 0 20px 0', color: '#0f172a' }}>
                                Cadastrar Novo Cartão com Segurança
                            </h3>

                            {/* Passamos o clientSecret gerado pela rota setup-intent */}
                            <Elements key={setupClientSecret} stripe={stripePromise} options={{ clientSecret: setupClientSecret }}>
                                <StripeCardForm
                                    userCpf={user?.cpf}
                                    onCancel={() => setShowAddCardForm(false)}
                                    onSuccess={() => {
                                        setShowAddCardForm(false); // Fecha o form
                                        buscarCartoesStripe();     // Recarrega a lista de cartões com o novo incluso
                                        carregarPerfil();          // Recarrega o perfil para atualizar o CPF na página
                                    }}
                                    onFailure={handleInitAddCard}
                                />
                            </Elements>
                        </div>
                    )}
                </div>
            )}

            {/* ========================================================================= */}
            {/* PAINEL: GRID DE PLANOS OFICIAIS                                           */}
            {/* ========================================================================= */}
            {viewMode === 'plans' && (
                <div style={{ animation: 'fadeIn 0.3s ease-out' }}>
                    {/* Inline Checkout Form se gerado um clientSecret */}
                    {clientSecret && (
                        <div style={{ maxWidth: '500px', margin: '0 auto 40px auto', backgroundColor: 'white', border: '1px solid #cbd5e1', borderRadius: '16px', padding: '24px', boxShadow: '0 10px 20px rgba(0,0,0,0.05)' }}>
                            <h3 style={{ margin: '0 0 15px 0', fontSize: '16px', fontWeight: '700' }}>Confirmando Checkout do {formatarPlano(checkoutPlan)}</h3>
                            <Elements key={clientSecret} stripe={stripePromise} options={{ clientSecret }}>
                                <StripePaymentForm
                                    onCancel={() => { setClientSecret(null); setCheckoutPlan(null); }}
                                    onSuccess={() => { setClientSecret(null); setCheckoutPlan(null); carregarPerfil(); }}
                                />
                            </Elements>
                        </div>
                    )}

                    {/* Toggle de Categoria: Afiliados vs Divulgadores */}
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', marginBottom: '35px' }}>
                        <div style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            backgroundColor: '#f1f5f9',
                            padding: '6px',
                            borderRadius: '50px',
                            border: '1px solid #e2e8f0',
                            gap: '6px',
                            boxShadow: '0 2px 8px rgba(0,0,0,0.04)'
                        }}>
                            <button
                                type="button"
                                id="btn-billing-tab-afiliados"
                                onClick={() => setPlanCategory('afiliados')}
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '8px',
                                    padding: '10px 26px',
                                    borderRadius: '40px',
                                    border: 'none',
                                    fontWeight: '800',
                                    fontSize: '15px',
                                    cursor: 'pointer',
                                    transition: 'all 0.25s ease',
                                    backgroundColor: planCategory === 'afiliados' ? '#ee4d2d' : 'transparent',
                                    color: planCategory === 'afiliados' ? '#ffffff' : '#64748b',
                                    boxShadow: planCategory === 'afiliados' ? '0 4px 14px rgba(238, 77, 45, 0.3)' : 'none'
                                }}
                            >
                                <ShoppingCart size={18} />
                                <span>Afiliados</span>
                            </button>

                            <button
                                type="button"
                                id="btn-billing-tab-divulgadores"
                                onClick={() => setPlanCategory('divulgadores')}
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '8px',
                                    padding: '10px 26px',
                                    borderRadius: '40px',
                                    border: 'none',
                                    fontWeight: '800',
                                    fontSize: '15px',
                                    cursor: 'pointer',
                                    transition: 'all 0.25s ease',
                                    backgroundColor: planCategory === 'divulgadores' ? '#1e293b' : 'transparent',
                                    color: planCategory === 'divulgadores' ? '#ffffff' : '#64748b',
                                    boxShadow: planCategory === 'divulgadores' ? '0 4px 14px rgba(30, 41, 59, 0.3)' : 'none'
                                }}
                            >
                                <Megaphone size={18} />
                                <span>Divulgadores</span>
                            </button>
                        </div>

                        <p style={{
                            margin: '14px 0 0 0',
                            fontSize: '14px',
                            color: '#64748b',
                            fontWeight: '500',
                            textAlign: 'center',
                            maxWidth: '560px'
                        }}>
                            {planCategory === 'afiliados'
                                ? 'Para quem divulga ofertas e links comissionados da Shopee, Magalu, Amazon, Mercado Livre, etc.'
                                : 'Para quem divulga conteúdos próprios, postagens de texto, fotos e vídeos para múltiplos grupos.'}
                        </p>
                    </div>

                    {planCategory === 'afiliados' ? (
                        <div key="afiliados" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '30px', alignItems: 'stretch', animation: 'fadeIn 0.3s ease-out' }}>
                            {/* 1. PLANO FREE (AFILIADO) */}
                            <div style={{ backgroundColor: 'white', border: '1px solid #e2e8f0', borderRadius: '20px', padding: '30px 24px', display: 'flex', flexDirection: 'column', justifyContent: 'space-between', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.02)', minHeight: '420px' }}>
                                <div>
                                    <span style={{ display: 'inline-block', fontSize: '10px', fontWeight: '800', color: '#64748b', textTransform: 'uppercase', backgroundColor: '#f1f5f9', padding: '4px 10px', borderRadius: '8px' }}>
                                        Experimentar
                                    </span>
                                    <h2 style={{ fontSize: '20px', fontWeight: '800', color: '#0f172a', margin: '15px 0 5px 0' }}>Plano Free</h2>
                                    <p style={{ fontSize: '12px', color: '#64748b', margin: 0 }}>Encurte e prepare suas ofertas manualmente.</p>

                                    <div style={{ margin: '20px 0', display: 'flex', alignItems: 'baseline', gap: '4px' }}>
                                        <span style={{ fontSize: '32px', fontWeight: '800', color: '#0f172a' }}>R$ 0</span>
                                        <span style={{ fontSize: '12px', color: '#64748b' }}>/mês</span>
                                    </div>

                                    <hr style={{ border: 0, borderTop: '1px solid #f1f5f9', margin: '15px 0' }} />

                                    <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: '10px', fontSize: '13px', color: '#334155' }}>
                                        <li style={{ display: 'flex', alignItems: 'center', gap: '8px' }}><Check size={14} style={{ color: '#22c55e', flexShrink: 0 }} /> Encurtador manual de links</li>
                                        <li style={{ display: 'flex', alignItems: 'center', gap: '8px' }}><Check size={14} style={{ color: '#22c55e', flexShrink: 0 }} /> Conversão com seu ID de afiliado</li>
                                        <li style={{ display: 'flex', alignItems: 'center', gap: '8px' }}><Check size={14} style={{ color: '#22c55e', flexShrink: 0 }} /> Painel de métricas básicas</li>
                                        <li style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#94a3b8' }}><X size={14} style={{ color: '#ef4444', flexShrink: 0 }} /> Sem automações de disparos</li>
                                    </ul>
                                </div>

                                <button
                                    onClick={() => handlePlanClick('free')}
                                    disabled={submittingPlan !== null || (!user?.plan || user?.plan === 'free')}
                                    style={{
                                        width: '100%', padding: '12px', borderRadius: '10px',
                                        border: (!user?.plan || user?.plan === 'free') ? '1px solid #cbd5e1' : '1px solid #ee4d2d',
                                        backgroundColor: (!user?.plan || user?.plan === 'free') ? '#f8fafc' : 'white',
                                        color: (!user?.plan || user?.plan === 'free') ? '#94a3b8' : '#ee4d2d',
                                        fontWeight: '700', fontSize: '13px',
                                        cursor: (!user?.plan || user?.plan === 'free') ? 'default' : 'pointer',
                                        marginTop: '25px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px'
                                    }}
                                >
                                    {submittingPlan === 'free' ? <RefreshCw size={14} className="custom-spin" /> : null}
                                    {(!user?.plan || user?.plan === 'free') ? 'Começar Grátis (Já Ativo)' : 'Mudar para Plano Free'}
                                </button>
                            </div>

                            {/* 2. PLANO BÁSICO (AFILIADO) */}
                            <div style={{ backgroundColor: 'white', border: '2px solid #ee4d2d', borderRadius: '20px', padding: '30px 24px', display: 'flex', flexDirection: 'column', justifyContent: 'space-between', boxShadow: '0 10px 15px -3px rgba(0,0,0,0.05)', position: 'relative', minHeight: '420px' }}>
                                <div style={{ position: 'absolute', top: '-11px', left: '20px', backgroundColor: '#ee4d2d', color: 'white', padding: '2px 10px', borderRadius: '8px', fontSize: '10px', fontWeight: '800', textTransform: 'uppercase' }}>
                                    Recomendado
                                </div>
                                <div>
                                    <span style={{ display: 'inline-block', fontSize: '10px', fontWeight: '800', color: '#ee4d2d', textTransform: 'uppercase', backgroundColor: '#fff5f1', padding: '4px 10px', borderRadius: '8px' }}>
                                        Crescimento
                                    </span>
                                    <h2 style={{ fontSize: '20px', fontWeight: '800', color: '#0f172a', margin: '15px 0 5px 0' }}>Plano Básico</h2>
                                    <p style={{ fontSize: '12px', color: '#64748b', margin: 0 }}>Excelente para afiliados em crescimento.</p>

                                    <div style={{ margin: '20px 0', display: 'flex', alignItems: 'baseline', gap: '4px' }}>
                                        <span style={{ fontSize: '32px', fontWeight: '800', color: '#0f172a' }}>R$ {priceBasic.toFixed(2).replace('.', ',')}</span>
                                        <span style={{ fontSize: '12px', color: '#64748b' }}>/mês</span>
                                    </div>

                                    <hr style={{ border: 0, borderTop: '1px solid #f1f5f9', margin: '15px 0' }} />

                                    <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: '10px', fontSize: '13px', color: '#334155' }}>
                                        <li style={{ display: 'flex', alignItems: 'center', gap: '8px' }}><Check size={14} style={{ color: '#22c55e', flexShrink: 0 }} /> Encaminhamento p/ WhatsApp pessoal</li>
                                        <li style={{ display: 'flex', alignItems: 'center', gap: '8px' }}><Check size={14} style={{ color: '#22c55e', flexShrink: 0 }} /> Integração com Bot do Telegram</li>
                                        <li style={{ display: 'flex', alignItems: 'center', gap: '8px' }}><Check size={14} style={{ color: '#22c55e', flexShrink: 0 }} /> Encurtador de links automático</li>
                                        <li style={{ display: 'flex', alignItems: 'center', gap: '8px' }}><Check size={14} style={{ color: '#22c55e', flexShrink: 0 }} /> Agendamento de posts 24h</li>
                                        <li style={{ display: 'flex', alignItems: 'center', gap: '8px' }}><Check size={14} style={{ color: '#22c55e', flexShrink: 0 }} /> Envio de fotos e vídeos nos grupos</li>
                                    </ul>
                                </div>

                                <button
                                    onClick={() => handlePlanClick('basic')}
                                    disabled={submittingPlan !== null || (user?.plan === 'basic' && !user?.cancel_at_period_end)}
                                    style={{
                                        width: '100%', padding: '12px', borderRadius: '10px', border: 'none',
                                        backgroundColor: (user?.plan === 'basic' && !user?.cancel_at_period_end) ? '#cbd5e1' : '#ee4d2d',
                                        color: 'white', fontWeight: '700', fontSize: '13px',
                                        cursor: (submittingPlan !== null || (user?.plan === 'basic' && !user?.cancel_at_period_end)) ? 'default' : 'pointer',
                                        marginTop: '25px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px'
                                    }}
                                >
                                    {submittingPlan === 'basic' ? <RefreshCw size={14} className="custom-spin" /> : <Zap size={14} />}
                                    {user?.plan === 'basic'
                                        ? (user?.cancel_at_period_end ? 'Reativar Plano Básico' : 'Seu Plano Atual')
                                        : (user?.plan === 'premium' ? 'Mudar para Básico' : 'Assinar Básico')}
                                </button>

                                {(!user?.plan || user?.plan === 'free' || user?.plan === 'free_divulgador') && !user?.trialUsed && (
                                    <button
                                        onClick={() => handleClaimTrialClick('basic')}
                                        style={{ width: '100%', padding: '10px', borderRadius: '10px', border: '2px solid #ee4d2d', backgroundColor: 'white', color: '#ee4d2d', fontWeight: '700', fontSize: '12px', cursor: 'pointer', marginTop: '10px' }}
                                    >
                                        Resgatar Amostra Grátis
                                    </button>
                                )}
                            </div>

                            {/* 3. PLANO PROFISSIONAL (AFILIADO) */}
                            <div style={{ backgroundColor: '#0f172a', border: '1px solid #1e293b', borderRadius: '20px', padding: '30px 24px', display: 'flex', flexDirection: 'column', justifyContent: 'space-between', boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1)', position: 'relative', color: 'white', minHeight: '420px' }}>
                                <div style={{ position: 'absolute', top: '-11px', left: '20px', backgroundColor: '#8b5cf6', color: 'white', padding: '2px 10px', borderRadius: '8px', fontSize: '10px', fontWeight: '800' }}>
                                    Automação Total
                                </div>
                                <div>
                                    <span style={{ display: 'inline-block', fontSize: '10px', fontWeight: '800', color: '#c084fc', textTransform: 'uppercase', letterSpacing: '0.05em', backgroundColor: 'rgba(139,92,246,0.15)', padding: '4px 10px', borderRadius: '8px' }}>
                                        Supremo
                                    </span>
                                    <h2 style={{ fontSize: '20px', fontWeight: '800', color: 'white', margin: '15px 0 5px 0' }}>Plano Profissional</h2>
                                    <p style={{ fontSize: '12px', color: '#94a3b8', margin: 0 }}>A solução suprema para grandes afiliados.</p>

                                    <div style={{ margin: '20px 0', display: 'flex', alignItems: 'baseline', gap: '4px' }}>
                                        <span style={{ fontSize: '32px', fontWeight: '800', color: 'white' }}>R$ {pricePremium.toFixed(2).replace('.', ',')}</span>
                                        <span style={{ fontSize: '12px', color: '#94a3b8' }}>/mês</span>
                                    </div>

                                    <hr style={{ border: 0, borderTop: '1px solid #1e293b', margin: '15px 0' }} />

                                    <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: '10px', fontSize: '13px', color: '#cbd5e1' }}>
                                        <li style={{ display: 'flex', alignItems: 'center', gap: '8px' }}><Check size={14} style={{ color: '#a78bfa', flexShrink: 0 }} /> WhatsApp pessoal + Bot do Telegram</li>
                                        <li style={{ display: 'flex', alignItems: 'center', gap: '8px', fontWeight: '700' }}><Check size={14} style={{ color: '#a78bfa', flexShrink: 0 }} /> Resposta Automática no Instagram</li>
                                        <li style={{ display: 'flex', alignItems: 'center', gap: '8px', fontWeight: '700' }}><Check size={14} style={{ color: '#a78bfa', flexShrink: 0 }} /> Automação de Comentários (Direct)</li>
                                        <li style={{ display: 'flex', alignItems: 'center', gap: '8px', fontWeight: '700' }}><Check size={14} style={{ color: '#a78bfa', flexShrink: 0 }} /> Suporte Prioritário VIP</li>
                                    </ul>
                                </div>

                                <button
                                    onClick={() => handlePlanClick('premium')}
                                    disabled={submittingPlan !== null || (user?.plan === 'premium' && !user?.cancel_at_period_end)}
                                    style={{
                                        width: '100%', padding: '12px', borderRadius: '10px', border: 'none',
                                        backgroundColor: (user?.plan === 'premium' && !user?.cancel_at_period_end) ? '#cbd5e1' : '#8b5cf6',
                                        color: 'white', fontWeight: '700', fontSize: '13px',
                                        cursor: (submittingPlan !== null || (user?.plan === 'premium' && !user?.cancel_at_period_end)) ? 'default' : 'pointer',
                                        marginTop: '25px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px'
                                    }}
                                >
                                    {submittingPlan === 'premium' ? <RefreshCw size={14} className="custom-spin" /> : <Sparkles size={14} />}
                                    {user?.plan === 'premium'
                                        ? (user?.cancel_at_period_end ? 'Reativar Plano Profissional' : 'Seu Plano Atual')
                                        : 'Assinar Profissional'}
                                </button>

                                {(!user?.plan || user?.plan === 'free' || user?.plan === 'free_divulgador') && !user?.trialUsed && (
                                    <button
                                        onClick={() => handleClaimTrialClick('premium')}
                                        style={{ width: '100%', padding: '10px', borderRadius: '10px', border: '2px solid #8b5cf6', backgroundColor: '#0f172a', color: '#8b5cf6', fontWeight: '700', fontSize: '12px', cursor: 'pointer', marginTop: '10px' }}
                                    >
                                        Resgatar Amostra Grátis
                                    </button>
                                )}
                            </div>
                        </div>
                    ) : (
                        <div key="divulgadores" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '30px', alignItems: 'stretch', animation: 'fadeIn 0.3s ease-out' }}>
                            {/* 1. PLANO FREE (DIVULGADOR) */}
                            <div style={{ backgroundColor: 'white', border: '1px solid #e2e8f0', borderRadius: '20px', padding: '30px 24px', display: 'flex', flexDirection: 'column', justifyContent: 'space-between', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.02)', minHeight: '420px' }}>
                                <div>
                                    <span style={{ display: 'inline-block', fontSize: '10px', fontWeight: '800', color: '#64748b', textTransform: 'uppercase', backgroundColor: '#f1f5f9', padding: '4px 10px', borderRadius: '8px' }}>
                                        Experimentar
                                    </span>
                                    <h2 style={{ fontSize: '20px', fontWeight: '800', color: '#0f172a', margin: '15px 0 5px 0' }}>Plano Free Divulgador</h2>
                                    <p style={{ fontSize: '12px', color: '#64748b', margin: 0 }}>Envie textos para 1 grupo com intervalo.</p>

                                    <div style={{ margin: '20px 0', display: 'flex', alignItems: 'baseline', gap: '4px' }}>
                                        <span style={{ fontSize: '32px', fontWeight: '800', color: '#0f172a' }}>R$ 0</span>
                                        <span style={{ fontSize: '12px', color: '#64748b' }}>/mês</span>
                                    </div>

                                    <hr style={{ border: 0, borderTop: '1px solid #f1f5f9', margin: '15px 0' }} />

                                    <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: '10px', fontSize: '13px', color: '#334155' }}>
                                        <li style={{ display: 'flex', alignItems: 'center', gap: '8px' }}><Check size={14} style={{ color: '#22c55e', flexShrink: 0 }} /> Postagens próprias de textos</li>
                                        <li style={{ display: 'flex', alignItems: 'center', gap: '8px' }}><Check size={14} style={{ color: '#22c55e', flexShrink: 0 }} /> Envio para 1 grupo ativo</li>
                                        <li style={{ display: 'flex', alignItems: 'center', gap: '8px' }}><Check size={14} style={{ color: '#22c55e', flexShrink: 0 }} /> Cooldown de 2h entre posts</li>
                                        <li style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#94a3b8' }}><X size={14} style={{ color: '#ef4444', flexShrink: 0 }} /> Sem envio de fotos e vídeos</li>
                                    </ul>
                                </div>

                                <button
                                    onClick={() => handlePlanClick('free_divulgador')}
                                    disabled={submittingPlan !== null || (user?.plan === 'free_divulgador')}
                                    style={{
                                        width: '100%', padding: '12px', borderRadius: '10px',
                                        border: (user?.plan === 'free_divulgador') ? '1px solid #cbd5e1' : '1px solid #3b82f6',
                                        backgroundColor: (user?.plan === 'free_divulgador') ? '#f8fafc' : 'white',
                                        color: (user?.plan === 'free_divulgador') ? '#94a3b8' : '#3b82f6',
                                        fontWeight: '700', fontSize: '13px',
                                        cursor: (user?.plan === 'free_divulgador') ? 'default' : 'pointer',
                                        marginTop: '25px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px'
                                    }}
                                >
                                    {submittingPlan === 'free_divulgador' ? <RefreshCw size={14} className="custom-spin" /> : null}
                                    {(user?.plan === 'free_divulgador') ? 'Começar Grátis (Já Ativo)' : 'Mudar para Free Divulgador'}
                                </button>
                            </div>

                            {/* 2. PLANO BÁSICO (DIVULGADOR) */}
                            <div style={{ backgroundColor: 'white', border: '2px solid #3b82f6', borderRadius: '20px', padding: '30px 24px', display: 'flex', flexDirection: 'column', justifyContent: 'space-between', boxShadow: '0 10px 15px -3px rgba(0,0,0,0.05)', position: 'relative', minHeight: '420px' }}>
                                <div style={{ position: 'absolute', top: '-11px', left: '20px', backgroundColor: '#3b82f6', color: 'white', padding: '2px 10px', borderRadius: '8px', fontSize: '10px', fontWeight: '800', textTransform: 'uppercase' }}>
                                    Grupos Ilimitados
                                </div>
                                <div>
                                    <span style={{ display: 'inline-block', fontSize: '10px', fontWeight: '800', color: '#3b82f6', textTransform: 'uppercase', backgroundColor: '#eff6ff', padding: '4px 10px', borderRadius: '8px' }}>
                                        Divulgação
                                    </span>
                                    <h2 style={{ fontSize: '20px', fontWeight: '800', color: '#0f172a', margin: '15px 0 5px 0' }}>Básico Divulgador</h2>
                                    <p style={{ fontSize: '12px', color: '#64748b', margin: 0 }}>Excelente para lojas e comércios locais.</p>

                                    <div style={{ margin: '20px 0', display: 'flex', alignItems: 'baseline', gap: '4px' }}>
                                        <span style={{ fontSize: '32px', fontWeight: '800', color: '#0f172a' }}>R$ {priceDivulgadorBasic.toFixed(2).replace('.', ',')}</span>
                                        <span style={{ fontSize: '12px', color: '#64748b' }}>/mês</span>
                                    </div>

                                    <hr style={{ border: 0, borderTop: '1px solid #f1f5f9', margin: '15px 0' }} />

                                    <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: '10px', fontSize: '13px', color: '#334155' }}>
                                        <li style={{ display: 'flex', alignItems: 'center', gap: '8px' }}><Check size={14} style={{ color: '#22c55e', flexShrink: 0 }} /> Postagens próprias de textos</li>
                                        <li style={{ display: 'flex', alignItems: 'center', gap: '8px' }}><Check size={14} style={{ color: '#22c55e', flexShrink: 0 }} /> Envio de fotos e vídeos nos grupos</li>
                                        <li style={{ display: 'flex', alignItems: 'center', gap: '8px' }}><Check size={14} style={{ color: '#22c55e', flexShrink: 0 }} /> Envio para grupos ilimitados</li>
                                        <li style={{ display: 'flex', alignItems: 'center', gap: '8px' }}><Check size={14} style={{ color: '#22c55e', flexShrink: 0 }} /> Sem limitações de horários</li>
                                    </ul>
                                </div>

                                <button
                                    onClick={() => handlePlanClick('basic_divulgador')}
                                    disabled={submittingPlan !== null || (user?.plan === 'basic_divulgador' && !user?.cancel_at_period_end)}
                                    style={{
                                        width: '100%', padding: '12px', borderRadius: '10px', border: 'none',
                                        backgroundColor: (user?.plan === 'basic_divulgador' && !user?.cancel_at_period_end) ? '#cbd5e1' : '#3b82f6',
                                        color: 'white', fontWeight: '700', fontSize: '13px',
                                        cursor: (submittingPlan !== null || (user?.plan === 'basic_divulgador' && !user?.cancel_at_period_end)) ? 'default' : 'pointer',
                                        marginTop: '25px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px'
                                    }}
                                >
                                    {submittingPlan === 'basic_divulgador' ? <RefreshCw size={14} className="custom-spin" /> : <Zap size={14} />}
                                    {user?.plan === 'basic_divulgador'
                                        ? (user?.cancel_at_period_end ? 'Reativar Plano Básico' : 'Seu Plano Atual')
                                        : (user?.plan === 'premium_divulgador' ? 'Mudar para Básico' : 'Assinar Básico')}
                                </button>

                                {(!user?.plan || user?.plan === 'free' || user?.plan === 'free_divulgador') && !user?.trialUsed && (
                                    <button
                                        onClick={() => handleClaimTrialClick('basic_divulgador')}
                                        style={{ width: '100%', padding: '10px', borderRadius: '10px', border: '2px solid #3b82f6', backgroundColor: 'white', color: '#3b82f6', fontWeight: '700', fontSize: '12px', cursor: 'pointer', marginTop: '10px' }}
                                    >
                                        Resgatar Amostra Grátis
                                    </button>
                                )}
                            </div>

                            {/* 3. PLANO PROFISSIONAL (DIVULGADOR) */}
                            <div style={{ backgroundColor: '#0f172a', border: '1px solid #1e293b', borderRadius: '20px', padding: '30px 24px', display: 'flex', flexDirection: 'column', justifyContent: 'space-between', boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1)', position: 'relative', color: 'white', minHeight: '420px' }}>
                                <div style={{ position: 'absolute', top: '-11px', left: '20px', backgroundColor: '#a855f7', color: 'white', padding: '2px 10px', borderRadius: '8px', fontSize: '10px', fontWeight: '800' }}>
                                    Acesso Total
                                </div>
                                <div>
                                    <span style={{ display: 'inline-block', fontSize: '10px', fontWeight: '800', color: '#c084fc', textTransform: 'uppercase', letterSpacing: '0.05em', backgroundColor: 'rgba(168,85,247,0.15)', padding: '4px 10px', borderRadius: '8px' }}>
                                        Premium
                                    </span>
                                    <h2 style={{ fontSize: '20px', fontWeight: '800', color: 'white', margin: '15px 0 5px 0' }}>Premium Divulgador</h2>
                                    <p style={{ fontSize: '12px', color: '#94a3b8', margin: 0 }}>Para portais de notícias e grandes influencers.</p>

                                    <div style={{ margin: '20px 0', display: 'flex', alignItems: 'baseline', gap: '4px' }}>
                                        <span style={{ fontSize: '32px', fontWeight: '800', color: 'white' }}>R$ {priceDivulgadorPremium.toFixed(2).replace('.', ',')}</span>
                                        <span style={{ fontSize: '12px', color: '#94a3b8' }}>/mês</span>
                                    </div>

                                    <hr style={{ border: 0, borderTop: '1px solid #1e293b', margin: '15px 0' }} />

                                    <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: '10px', fontSize: '13px', color: '#cbd5e1' }}>
                                        <li style={{ display: 'flex', alignItems: 'center', gap: '8px' }}><Check size={14} style={{ color: '#c084fc', flexShrink: 0 }} /> Texto, Imagens e Vídeos liberados</li>
                                        <li style={{ display: 'flex', alignItems: 'center', gap: '8px', fontWeight: '700' }}><Check size={14} style={{ color: '#c084fc', flexShrink: 0 }} /> Grupos e horários ilimitados</li>
                                        <li style={{ display: 'flex', alignItems: 'center', gap: '8px', fontWeight: '700' }}><Check size={14} style={{ color: '#c084fc', flexShrink: 0 }} /> Instagram, WhatsApp e Telegram</li>
                                        <li style={{ display: 'flex', alignItems: 'center', gap: '8px', fontWeight: '700' }}><Check size={14} style={{ color: '#c084fc', flexShrink: 0 }} /> Suporte Prioritário VIP</li>
                                    </ul>
                                </div>

                                <button
                                    onClick={() => handlePlanClick('premium_divulgador')}
                                    disabled={submittingPlan !== null || (user?.plan === 'premium_divulgador' && !user?.cancel_at_period_end)}
                                    style={{
                                        width: '100%', padding: '12px', borderRadius: '10px', border: 'none',
                                        backgroundColor: (user?.plan === 'premium_divulgador' && !user?.cancel_at_period_end) ? '#cbd5e1' : '#a855f7',
                                        color: 'white', fontWeight: '700', fontSize: '13px',
                                        cursor: (submittingPlan !== null || (user?.plan === 'premium_divulgador' && !user?.cancel_at_period_end)) ? 'default' : 'pointer',
                                        marginTop: '25px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px'
                                    }}
                                >
                                    {submittingPlan === 'premium_divulgador' ? <RefreshCw size={14} className="custom-spin" /> : <Sparkles size={14} />}
                                    {user?.plan === 'premium_divulgador'
                                        ? (user?.cancel_at_period_end ? 'Reativar Plano Premium' : 'Seu Plano Atual')
                                        : 'Assinar Premium'}
                                </button>

                                {(!user?.plan || user?.plan === 'free' || user?.plan === 'free_divulgador') && !user?.trialUsed && (
                                    <button
                                        onClick={() => handleClaimTrialClick('premium_divulgador')}
                                        style={{ width: '100%', padding: '10px', borderRadius: '10px', border: '2px solid #a855f7', backgroundColor: '#0f172a', color: '#a855f7', fontWeight: '700', fontSize: '12px', cursor: 'pointer', marginTop: '10px' }}
                                    >
                                        Resgatar Amostra Grátis
                                    </button>
                                )}
                            </div>
                        </div>
                    )}
                </div>
            )}

            {/* Selo de Segurança Rodapé */}
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '15px', color: '#64748b', fontSize: '13px', textAlign: 'center', marginTop: '40px' }}>
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}><ShieldCheck size={16} style={{ color: '#22c55e' }} /> Ambiente Totalmente Criptografado e Protegido por Stripe Inc.</span>
            </div>

        </div>
    );
};

export default BillingPage;